/**
 * Auto-discovery of prototypes using Vite's import.meta.glob.
 *
 * Prototypes live in one of three context subdirectories:
 *   - `src/prototypes/size2mix/<slug>/index.tsx`
 *   - `src/prototypes/urbanmap/<slug>/index.tsx`
 *   - `src/prototypes/plan2cost/<slug>/index.tsx` (Plan — development plans built on a UrbanMap copy)
 *
 * Each must export a default React component and optionally `meta: PrototypeMeta`.
 * The slug is derived from the leaf directory name.
 */
import type { ComponentType } from 'react';
import { PROTOTYPE_STATUS_ORDER } from './prototypeStatus';
import type { PrototypeStatus } from './prototypeStatus';

export type PrototypeContext = 'size2mix' | 'urbanmap' | 'plan2cost';

export interface PrototypeMeta {
  title: string;
  description: string;
  tags?: string[];
  author?: string;
  /** `reference` is reserved for pages verified against the real product, not proposed variants. */
  status?: PrototypeStatus;
  /**
   * Nemo page, optionally followed by a sub-page: `page` or `page/sous-page`
   * (e.g. 'general', 'topology/parameters', 'optimisations/logs').
   * See `CATEGORIES` in `pages/home/constants.ts` (Size2Mix catalogue).
   */
  category?: string;
  /** ISO date (YYYY-MM-DD) when the prototype was first created. */
  createdAt?: string;
  /** ISO date (YYYY-MM-DD) or ISO 8601 date-time with an explicit timezone; update with source changes. */
  updatedAt?: string;
}

export interface DiscoveredPrototype {
  slug: string;
  context: PrototypeContext;
  meta: PrototypeMeta;
  loader: () => Promise<{ default: ComponentType }>;
}

interface PrototypeMetaModule {
  meta?: PrototypeMeta;
}

function buildList(
  metaModules: Record<string, PrototypeMetaModule | undefined>,
  componentLoaders: Record<string, () => Promise<{ default: ComponentType }>>,
  context: PrototypeContext,
): DiscoveredPrototype[] {
  const protos: DiscoveredPrototype[] = [];
  for (const [path, maybeMeta] of Object.entries(metaModules)) {
    const slugMatch = path.match(/\/prototypes\/[^/]+\/([^/]+)\/index\.tsx$/);
    if (!slugMatch) continue;
    const slug = slugMatch[1];
    const meta: PrototypeMeta = (maybeMeta as PrototypeMeta) ?? {
      title: slug,
      description: 'No description provided. Add `export const meta = {...}` to index.tsx.',
    };
    const loader = componentLoaders[path];
    if (!loader) continue;
    protos.push({ slug, context, meta, loader });
  }
  return protos.sort((a, b) => {
    const sa = PROTOTYPE_STATUS_ORDER[a.meta.status ?? 'draft'];
    const sb = PROTOTYPE_STATUS_ORDER[b.meta.status ?? 'draft'];
    if (sa !== sb) return sa - sb;
    return a.meta.title.localeCompare(b.meta.title);
  });
}

// ── Size2Mix ──────────────────────────────────────────────────────────────────

const s2mMetaModules = import.meta.glob<PrototypeMetaModule>(
  '../prototypes/size2mix/*/index.tsx',
  { eager: true, import: 'meta' },
);
const s2mLoaders = import.meta.glob<{ default: ComponentType }>(
  '../prototypes/size2mix/*/index.tsx',
);

// ── Urban Map ─────────────────────────────────────────────────────────────────

const urbanMapMetaModules = import.meta.glob<PrototypeMetaModule>(
  '../prototypes/urbanmap/*/index.tsx',
  { eager: true, import: 'meta' },
);
const urbanMapLoaders = import.meta.glob<{ default: ComponentType }>(
  '../prototypes/urbanmap/*/index.tsx',
);

// ── Plan (plan2cost) ──────────────────────────────────────────────────────────

const planMetaModules = import.meta.glob<PrototypeMetaModule>(
  '../prototypes/plan2cost/*/index.tsx',
  { eager: true, import: 'meta' },
);
const planLoaders = import.meta.glob<{ default: ComponentType }>(
  '../prototypes/plan2cost/*/index.tsx',
);

export function discoverPrototypes(context: PrototypeContext): DiscoveredPrototype[] {
  if (context === 'plan2cost') {
    return buildList(planMetaModules, planLoaders, 'plan2cost');
  }
  if (context === 'urbanmap') {
    return buildList(urbanMapMetaModules, urbanMapLoaders, 'urbanmap');
  }
  return buildList(s2mMetaModules, s2mLoaders, 'size2mix');
}
